
import { Comment } from '../types/comment';
import { supabase } from '@/lib/supabase/client';

interface AIGeneratedReply {
  id: string;
  content: string;
  category?: string;
}

interface GenerationResult {
  success: boolean;
  reply?: string;
  error?: string;
}

/**
 * Génère des réponses AI basées sur le commentaire
 */
export const generateAIResponses = async (comment: Comment): Promise<string[]> => {
  if (!comment.content) {
    return [];
  }
  
  try {
    const commentContent = comment.content.toLowerCase();
    const responses: string[] = [];
    
    // Première réponse
    if (commentContent.includes('merci') || commentContent.includes('thank') || 
        commentContent.includes('super') || commentContent.includes('bien')) {
      responses.push("Merci pour votre commentaire ! Nous sommes ravis que vous ayez apprécié notre contenu.");
    }
    
    // Deuxième réponse 
    if (commentContent.includes('question') || commentContent.includes('?')) {
      responses.push("Merci pour votre question. Nous allons examiner cela et vous revenir dès que possible avec une réponse.");
    } else if (commentContent.includes('problème') || commentContent.includes('problem')) {
      responses.push("Je suis désolé d'apprendre que vous rencontrez des difficultés. Pourriez-vous nous donner plus de détails pour que nous puissions vous aider au mieux?");
    }
    
    // Troisième réponse générique
    responses.push("Merci pour votre commentaire! Nous apprécions votre retour et espérons vous revoir bientôt sur notre chaîne.");
    
    // Quatrième réponse plus personnalisée
    if (comment.status === 'positive') {
      responses.push(`Nous sommes ravis que vous ayez apprécié notre vidéo sur "${comment.videoTitle}"! N'hésitez pas à vous abonner pour plus de contenu similaire.`);
    }
    
    return responses;
  } catch (error) {
    console.error('Erreur lors de la génération des réponses AI:', error);
    return ["Merci pour votre commentaire! Nous apprécions votre retour."];
  }
};

/**
 * Generates an AI reply based on the comment content using basic pattern matching
 */
export const generateSimpleAIReply = async (comment: Comment): Promise<GenerationResult> => {
  if (!comment.content) {
    return { success: false, error: "Le commentaire n'a pas de contenu" };
  }
  
  try {
    const commentContent = comment.content.toLowerCase();
    let generatedReply = "";
    
    if (commentContent.includes('merci') || commentContent.includes('thank')) {
      generatedReply = "Merci pour votre commentaire! Nous sommes ravis que vous ayez apprécié notre contenu.";
    } else if (commentContent.includes('problème') || commentContent.includes('problem') || commentContent.includes('issue')) {
      generatedReply = "Je suis désolé d'apprendre que vous rencontrez des difficultés. Pourriez-vous nous donner plus de détails pour que nous puissions vous aider au mieux?";
    } else if (commentContent.includes('question') || commentContent.includes('?')) {
      generatedReply = "Merci pour votre question. Nous allons examiner cela et vous revenir dès que possible avec une réponse.";
    } else if (commentContent.includes('aime') || commentContent.includes('like') || commentContent.includes('génial') || commentContent.includes('awesome')) {
      generatedReply = "Nous sommes ravis que vous ayez apprécié notre contenu! N'hésitez pas à vous abonner pour plus de vidéos similaires.";
    } else {
      generatedReply = "Merci pour votre commentaire! Nous apprécions votre retour et espérons vous revoir bientôt sur notre chaîne.";
    }
    
    return { success: true, reply: generatedReply };
  } catch (error) {
    console.error('Erreur lors de la génération de la réponse:', error);
    return { success: false, error: "Impossible de générer une réponse automatique" };
  }
};

/**
 * Fetches AI-generated replies from the database
 * @param platform The social media platform
 * @param commentId Optional comment ID to fetch specific responses
 */
export const fetchAIGeneratedReplies = async (platform: string, commentId?: string): Promise<AIGeneratedReply[]> => {
  try {
    let query = supabase
      .from('comments')
      .select('id, content')
      .eq('id', 'ai_generated_reply')
      .eq('platform', platform);
    
    // If a commentId is provided, filter for responses specific to this comment
    if (commentId) {
      query = query.eq('post_id', commentId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Erreur lors de la récupération des réponses IA:', error);
      return [];
    }
    
    if (data && data.length > 0) {
      return data.map(item => ({
        id: item.id,
        content: item.content,
        category: 'AI Generated'
      }));
    }
    
    return [];
  } catch (error) {
    console.error('Exception lors de la récupération des réponses IA:', error);
    return [];
  }
};
